/**
 * Report Builder - AI Summary for Report Description
 *
 * Generates AI summary of the entire report based on all charts and content
 */

(function () {
  "use strict";

  /**
   * Initialize AI summary functionality
   */
  function initReportAiSummary() {
    const summaryBtn = document.querySelector(".btn-generate-report-summary");

    if (!summaryBtn) {
      return;
    }

    summaryBtn.addEventListener("click", async function () {
      await generateReportSummary();
    });
  }

  /**
   * Collect all report data for AI summary
   */
  function collectReportData() {
    const canvasCharts = document.getElementById("canvasCharts");
    if (!canvasCharts) {
      return null;
    }

    const reportData = {
      charts: [],
      textBlocks: [],
      faqs: [],
      images: [],
      calculators: [],
      patientStories: [],
    };

    // Collect all chart data
    const chartItems = canvasCharts.querySelectorAll(".canvas-chart-item");
    chartItems.forEach((chartItem, index) => {
      const chartId = chartItem.getAttribute("data-chart-id");
      const titleEl = chartItem.querySelector("[data-base-title]");
      const title = titleEl ? titleEl.textContent.trim() : `Chart ${index + 1}`;

      // Get chart data from MchriChartAi store if available
      const chartContainerId = `chart_${chartId}`;
      const chartData = window.MchriChartAi?.charts?.[chartContainerId];

      // Get any existing AI summary for this chart
      const aiSummaryEl = chartItem.querySelector(".chart-ai-summary-text");
      const existingSummary = aiSummaryEl
        ? aiSummaryEl.textContent.trim()
        : null;

      // Get filter information
      let filters = null;
      if (chartItem) {
        const startDateInput = chartItem.querySelector(".filter-start-date");
        const endDateInput = chartItem.querySelector(".filter-end-date");
        const wardSelect = chartItem.querySelector(".filter-ward");

        filters = {
          startDate: startDateInput?.value || null,
          endDate: endDateInput?.value || null,
          ward: wardSelect?.options?.[wardSelect.selectedIndex]?.text || null,
        };
      }

      // Enhance chart data with filters
      const enhancedChartData = chartData
        ? {
            ...chartData,
            filters: filters,
          }
        : null;

      reportData.charts.push({
        title,
        chartId,
        data: enhancedChartData,
        summary: existingSummary,
      });
    });

    // Collect text blocks
    const textItems = canvasCharts.querySelectorAll(".canvas-text-item");
    textItems.forEach((textItem, index) => {
      const titleEl = textItem.querySelector(".text-item-header h4");
      const title = titleEl
        ? titleEl.textContent.trim()
        : `Text Block ${index + 1}`;

      const contentEl = textItem.querySelector(".text-item-content");
      let content = "";

      if (contentEl) {
        // Try to get content from WYSIWYG textarea first
        const textarea = contentEl.querySelector(".wysiwyg-textarea");
        if (textarea && textarea.value) {
          content = textarea.value.trim();
        } else {
          // Try to get from iframe (TinyMCE)
          const iframe = contentEl.querySelector("iframe");
          if (iframe && iframe.contentDocument) {
            content = iframe.contentDocument.body.textContent.trim();
          } else {
            // Fallback to direct text content
            content = contentEl.textContent.trim();
          }
        }
      }

      if (content) {
        reportData.textBlocks.push({
          title,
          content,
        });
      }
    });

    // Collect FAQ items (Fun Facts)
    const faqItems = canvasCharts.querySelectorAll(".canvas-faq-item");
    faqItems.forEach((faqItem, index) => {
      const titleEl = faqItem.querySelector(".faq-item-header h4");
      const title = titleEl
        ? titleEl.textContent.trim()
        : `Fun Fact ${index + 1}`;

      // Get FAQ content
      const contentArea = faqItem.querySelector(".faq-item-content");
      let content = "";

      if (contentArea) {
        const answerContent = contentArea.querySelector(".faq-answer-content");
        if (answerContent) {
          content = answerContent.textContent.trim();
        } else {
          content = contentArea.textContent.trim();
        }
      }

      if (content) {
        reportData.faqs.push({
          title,
          content,
        });
      }
    });

    // Collect image items
    const imageItems = canvasCharts.querySelectorAll(".canvas-image-item");
    imageItems.forEach((imageItem, index) => {
      const titleEl = imageItem.querySelector(".image-item-header h4");
      const title = titleEl ? titleEl.textContent.trim() : `Image ${index + 1}`;

      const captionEl = imageItem.querySelector(".image-caption");
      const caption = captionEl ? captionEl.textContent.trim() : "";

      const altText = imageItem.querySelector("img")?.alt;

      reportData.images.push({
        title,
        altText: altText || "",
        caption,
      });
    });

    // Collect calculator items
    const calculatorItems = canvasCharts.querySelectorAll(
      ".canvas-calculator-item"
    );
    calculatorItems.forEach((calcItem, index) => {
      const titleEl = calcItem.querySelector(".calculator-item-header h4");
      const title = titleEl
        ? titleEl.textContent.trim()
        : `Calculator ${index + 1}`;
      const costValue = calcItem
        .querySelector(".cost-calculator-result-value")
        ?.textContent?.trim();
      const carbonValue = calcItem
        .querySelector(".carbon-calculator-result-value")
        ?.textContent?.trim();

      reportData.calculators.push({
        title,
        calculatorType: costValue
          ? "dollar value saved"
          : "amount of carbon saved in kg",
        value: costValue || carbonValue || "N/A",
      });
    });

    // Collect patient stories
    const storyItems = canvasCharts.querySelectorAll(
      ".canvas-patient-story-item"
    );
    storyItems.forEach((storyItem, index) => {
      const titleEl = storyItem.querySelector(".patient-story-item-header h4");
      const title = titleEl
        ? titleEl.textContent.trim()
        : `Patient Story ${index + 1}`;

      const contentEl = storyItem.querySelector(".patient-story-item-content");
      const content = contentEl ? contentEl.textContent.trim() : "";

      if (content) {
        reportData.patientStories.push({
          title,
          content,
        });
      }
    });

    return reportData;
  }

  /**
   * Build AI prompt for report summary
   */
  function buildReportSummaryPrompt(reportData) {
    const parts = [];

    parts.push(
      "use Australian English spelling and grammar"
    );
    parts.push("");
    parts.push(
      "Please provide a concise summary (up to 5 sentences but less is okay if it's a short report) of this healthcare report based on ALL the following content:"
    );
    parts.push("");

    // Add chart summaries with detailed data
    if (reportData.charts.length > 0) {
      parts.push("=== CHARTS AND DATA ANALYSIS ===");
      reportData.charts.forEach((chart, index) => {
        parts.push(`\nChart ${index + 1}: ${chart.title}`);

        if (chart.summary) {
          parts.push(`AI Analysis: ${chart.summary}`);
        }

        if (chart.data) {
          if (chart.data.chartType) {
            parts.push(`Chart Type: ${chart.data.chartType}`);
          }

          if (chart.data.totalResponses) {
            parts.push(`Total Responses: ${chart.data.totalResponses}`);
          }

          // Include actual data points if available
          if (chart.data.labels && chart.data.datasets) {
            parts.push("Data Points:");
            chart.data.labels.forEach((label, i) => {
              const values = chart.data.datasets
                .map((ds) => {
                  const value = ds.data[i];
                  return `${ds.label || "Value"}: ${value}`;
                })
                .join(", ");
              parts.push(`  - ${label}: ${values}`);
            });
          }

          // Include filters applied
          if (chart.data.filters) {
            const filterParts = [];
            if (chart.data.filters.startDate)
              filterParts.push(`Start: ${chart.data.filters.startDate}`);
            if (chart.data.filters.endDate)
              filterParts.push(`End: ${chart.data.filters.endDate}`);
            if (chart.data.filters.ward)
              filterParts.push(`Ward: ${chart.data.filters.ward}`);
            if (filterParts.length > 0) {
              parts.push(`Filters Applied: ${filterParts.join(", ")}`);
            }
          }
        }
      });
      parts.push("");
    }

    // Add text blocks with full content
    if (reportData.textBlocks.length > 0) {
      parts.push("=== TEXT CONTENT ===");
      reportData.textBlocks.forEach((block, index) => {
        parts.push(`\nText Block ${index + 1}: ${block.title}`);
        // Include more content (up to 500 chars instead of 200)
        const content =
          block.content.length > 500
            ? block.content.substring(0, 500) + "..."
            : block.content;
        parts.push(content);
      });
      parts.push("");
    }

    // Add Fun Facts (FAQs)
    if (reportData.faqs.length > 0) {
      parts.push("=== FUN FACTS ===");
      reportData.faqs.forEach((faq, index) => {
        parts.push(`\nFun Fact ${index + 1}: ${faq.title}`);
        if (faq.content) {
          const content =
            faq.content.length > 500
              ? faq.content.substring(0, 500) + "..."
              : faq.content;
          parts.push(content);
        }
      });
      parts.push("");
    }

    // Add images with captions
    if (reportData.images.length > 0) {
      parts.push("=== IMAGES ===");
      reportData.images.forEach((img, index) => {
        parts.push(`Image ${index + 1}: ${img.title}`);
        if (img.caption) {
          parts.push(`Caption: ${img.caption}`);
        }

        if (img.altText) {
          parts.push(`Alt Text: ${img.altText}`);
        }
      });
      parts.push("");
    }

    // Add calculator results
    if (reportData.calculators.length > 0) {
      parts.push("=== CALCULATORS ===");
      reportData.calculators.forEach((calc, index) => {
        parts.push(`Calculator ${index + 1}: ${calc.title}`);
        parts.push(`Calculator Type: ${calc.calculatorType}`);
        parts.push(`Value: ${calc.value}`);
      });
      parts.push("");
    }

    // Add patient stories
    if (reportData.patientStories.length > 0) {
      parts.push("=== PATIENT STORIES ===");
      reportData.patientStories.forEach((story, index) => {
        parts.push(`Story ${index + 1}: ${story.title}`);
        const content =
          story.content.length > 300
            ? story.content.substring(0, 300) + "..."
            : story.content;
        parts.push(content);
      });
      parts.push("");
    }

    parts.push("===");
    parts.push("");
    parts.push(
      "Based on ALL the above content (charts, data, text, Fun Facts, images, calculators, and patient stories), generate a professional, concise summary (up to 5 sentences but less is okay if it's a short report) that:"
    );
    parts.push("1. Captures the main purpose of this healthcare report");
    parts.push("2. Highlights the key findings and data insights");
    parts.push(
      "3. Mentions the scope of the analysis (time period, wards, metrics covered)"
    );
    parts.push("4. Explains how the each block relates to the overall report");

    return parts.join("\n");
  }

  /**
   * Request AI summary from backend
   */
  async function requestAiSummary(promptString) {
    if (typeof chatgptAnalyzer === "undefined") {
      throw new Error("ChatGPT analyzer is not available on this page.");
    }

    const formData = new FormData();
    formData.append("action", "analyze_with_chatgpt");
    formData.append("nonce", chatgptAnalyzer.nonce);
    formData.append("data", promptString);

    const response = await fetch(chatgptAnalyzer.ajaxurl, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `AI request failed (HTTP ${response.status}): ${errorText}`
      );
    }

    const result = await response.json();
    if (!result.success) {
      throw new Error(result.data?.message || "AI request failed.");
    }

    return result.data.analysis || "";
  }

  /**
   * Generate report summary
   */
  async function generateReportSummary() {
    const summaryBtn = document.querySelector(".btn-generate-report-summary");
    const descriptionEl = document.querySelector(".canvas-report-description");

    if (!summaryBtn || !descriptionEl) {
      return;
    }

    // Collect report data
    const reportData = collectReportData();

    if (!reportData) {
      alert("Unable to collect report data. Please try again.");
      return;
    }

    // Debug: Log collected data
    console.log("Collected Report Data:", reportData);
    console.log("Charts:", reportData.charts.length);
    console.log("Text Blocks:", reportData.textBlocks.length);
    console.log("FAQs:", reportData.faqs.length);
    console.log("Images:", reportData.images.length);
    console.log("Calculators:", reportData.calculators.length);
    console.log("Patient Stories:", reportData.patientStories.length);

    // Check if report has any content
    const hasContent =
      reportData.charts.length > 0 ||
      reportData.textBlocks.length > 0 ||
      reportData.faqs.length > 0 ||
      reportData.images.length > 0 ||
      reportData.calculators.length > 0 ||
      reportData.patientStories.length > 0;

    if (!hasContent) {
      alert(
        "Please add some content to the report before generating a summary."
      );
      return;
    }

    // Build prompt
    const promptString = buildReportSummaryPrompt(reportData);

    // Debug: Log the prompt
    console.log("+#+#+#+ AI Prompt:", promptString);

    // Update UI
    const originalBtnText = summaryBtn.innerHTML;
    summaryBtn.disabled = true;
    summaryBtn.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="spinning"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg> Generating...';

    const originalDescription = descriptionEl.textContent;

    try {
      const summary = await requestAiSummary(promptString);

      // Update description with summary
      descriptionEl.textContent = summary;

      // Show success feedback
      summaryBtn.innerHTML =
        '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg> Summary Generated!';

      // Reset button after 2 seconds
      setTimeout(() => {
        summaryBtn.innerHTML = originalBtnText;
        summaryBtn.disabled = false;
      }, 2000);
    } catch (err) {
      console.error("AI summary error:", err);

      // Show error
      summaryBtn.innerHTML =
        '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg> Error';

      alert(`Failed to generate summary: ${err.message}`);

      // Reset button
      setTimeout(() => {
        summaryBtn.innerHTML = originalBtnText;
        summaryBtn.disabled = false;
      }, 2000);
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initReportAiSummary);
  } else {
    initReportAiSummary();
  }

  // Expose to global scope if needed
  window.ReportBuilderAiSummary = {
    init: initReportAiSummary,
    generateSummary: generateReportSummary,
  };
})();
